/**
 * Shared vocabulary and rules for the player's health record (issue #287).
 *
 * TWO surfaces render this data and they render it for different people:
 * the player's own entry card in Settings (`HealthInfoCard.jsx`) and the
 * coach's read-only panel on the player they are viewing
 * (`PlayerHealthPanel.jsx`). Both the sharing sentence and the empty-state
 * word are safety copy, so they live here as named exports rather than as
 * strings typed into two components — the failure this guards against is a
 * third surface shipping with softer wording, and a named import is the
 * thing a reviewer notices missing.
 *
 * ============================================================
 * "NOT PROVIDED" IS NOT "NONE". THIS IS THE WHOLE MODULE.
 * ============================================================
 * An empty health field means nobody typed anything. It does NOT mean the
 * player has no conditions and it does NOT mean there is nobody to call,
 * and a coach who reads it either of those ways is the exact harm this
 * feature exists to prevent. So:
 *   - `NOT_PROVIDED` is the only string either surface may render for an
 *     absent value. Never "None", never "No known conditions", never an
 *     em-dash, and never a blank line (a blank reads as "nothing to
 *     report", which is the same false claim with no words in it).
 *   - `hasAnyHealthContent` exists so a surface can tell "this player has
 *     told us something" from "this player has told us nothing" WITHOUT
 *     either answer being rendered as reassurance.
 *
 * ============================================================
 * WHO, RIGHT NOW — issue #694, following #680's audience-global decision
 * ============================================================
 * `SHARING_NOTICE` below is a true statement of the RULE — "your linked
 * coaches can see this" — but it is not a statement of the CURRENT fact,
 * and #680's own closing argument is that the gap between those two is
 * exactly what a player with a club coach and a private coach misreads.
 * `describeHealthAudience` is the factual sentence: it names who the rule
 * resolves to right now, sourced from the server the same way
 * `identitySharing.js`'s `describeVisibility` does — never assembled from a
 * client-side coach list, which could disagree with the predicate that
 * actually gates the coach's read.
 */

import { joinNames } from "./identitySharing";

/** The one string for an absent value, on every surface. See the header. */
export const NOT_PROVIDED = "Not provided";

/**
 * The sharing statement, shown ON the entry card, at the point of entry —
 * Martin's exact wording, 2026-08-18. Sharing is automatic to every ACTIVE
 * linked coach with no per-coach opt-in, and the price of that simplicity
 * is that the player is told plainly, where they type, not in a policy
 * page.
 *
 * IT NAMES THE EMERGENCY CONTACT EXPLICITLY, and that is not padding: the
 * named contact is usually a PARENT who has no account here and has
 * consented to nothing. "Health information" would let a player think they
 * were sharing facts about themselves. They are also sharing somebody
 * else's phone number, and they should know that before they type it.
 *
 * "Linked" and not "your club": there is no club entity (#158), so a
 * sentence promising club-level control would describe something that does
 * not exist.
 */
export const SHARING_NOTICE = "Your linked coaches can see this, including your emergency contact.";

/**
 * The honest detail. A coach with an active link sees this the moment the
 * player saves it — there is no approval step, no notification, and no
 * per-coach switch. Unlinking the coach is the control, and it is one the
 * player already understands.
 *
 * THIS SENTENCE USED TO SAY "not platform admins", FULL STOP, AND THAT WAS
 * AN OVERCLAIM. An adversarial review (2026-08-18) walked the path: an
 * admin can reset a linked coach's password (`routes/adminUsers.ts`), sign
 * in as that coach, and read whatever that coach can read — this record
 * included. The API's admin exclusion is real and holds (admin gets a 404
 * on every health route, and no admin screen renders the record), but it
 * is a guard on the ADMIN ROLE, not on a determined administrator.
 *
 * #205's honesty rule applies and decides this: copy may not imply a
 * guarantee the system does not make. So the clause stays — it is useful
 * and true that no admin screen shows this — and the limit is named in the
 * same breath rather than left for someone to discover.
 *
 * The takeover is also NOT SILENT (#1440, D373), and saying so is not softening
 * it: a password reset or a sign-in email change ends every session on the
 * coach's account, a reset stops the old password working and forces a new
 * one at next sign-in, an email change sends a notice to the old address,
 * and the action is recorded with the time and the accounts involved. A player deciding what to write is
 * better served knowing the risk exists and is not silent than by either
 * an absolute promise or an unqualified alarm.
 *
 * `routes/playerHealth.ts` carries the same limit in its own honest-limits
 * list, next to the operator-database and #223-backup ones. If you change
 * one, change the other.
 */
export const SHARING_DETAIL =
  "Coaches you've linked to see this as soon as you save it. It isn't shown to other players, and no " +
  "admin screen displays it — though an administrator could reset a linked coach's password or change " +
  "their sign-in email and sign in as them. That signs the coach out everywhere and it is " +
  "recorded. After a reset the old password stops working; an email change sends a notice to the old " +
  "address. To stop sharing it, remove the coach's access.";

/**
 * Told to the coach, on their panel, in the same breath as the data. It is
 * the counterweight to "the app showed me nothing, so he's fine".
 */
export const COACH_ABSENCE_WARNING =
  "\"Not provided\" means nobody has filled this in — it does not mean there is nothing to know. Ask.";

// PR #980 round 3, N2 — round 2's U1 fix changed this constant in place to
// curly quotes, which leaked into the SHIPPED (flag-off) panel too: this
// same constant, same testid (`cp-health-absence-warning`), is what
// `PlayerHealthPanel.jsx`'s DEFAULT variant renders — the copy-check that
// found the mismatch was reading only the drawn `CoachPlayer.dc.html`
// artboard, and correcting the shared source changed BOTH readers at once.
// `COACH_ABSENCE_WARNING` above is restored to its shipped, straight-quote
// wording; this is the drawn variant only, used by
// `PlayerHealthPanel.jsx` when `variant === "coach-player"` and nowhere
// else — never imported by the shipped panel, `HealthInfoCard.jsx`, or
// `settingsSummary.js`, none of which use curly quotes for this sentence.
export const DRAWN_COACH_ABSENCE_WARNING =
  "“Not provided” means nobody has filled this in — it does not mean there is nothing to know. Ask.";

/** The character caps, mirroring the API's Zod schema and the DB CHECKs. */
export const CONTACT_NAME_MAX = 120;
export const CONTACT_RELATIONSHIP_MAX = 60;
export const CONTACT_PHONE_MAX = 40;
export const CONTACT_ALT_MAX = 120;
export const CONDITIONS_MAX = 2000;
export const NEEDS_MAX = 2000;

/**
 * The six content fields, IN THE ORDER BOTH SURFACES RENDER THEM.
 *
 * Emergency contact first — Martin's decision, 2026-08-18: *"emergency
 * contact first in the card, conditions and accessibility needs below. It
 * is the field most likely to be needed in a hurry and the least sensitive
 * of the three."* This constant is the single source of that ordering so
 * the entry card and the coach's panel cannot drift apart; a coach who has
 * learned where to look on their own screen should find it in the same
 * place the player left it.
 */
export const HEALTH_FIELDS = [
  "emergency_contact_name",
  "emergency_contact_relationship",
  "emergency_contact_phone",
  "emergency_contact_alt",
  "medical_conditions",
  "accessibility_needs",
];

/** Every content field blank — what an untouched record looks like locally. */
export const EMPTY_HEALTH_FORM = {
  emergency_contact_name: "",
  emergency_contact_relationship: "",
  emergency_contact_phone: "",
  emergency_contact_alt: "",
  medical_conditions: "",
  accessibility_needs: "",
};

/**
 * True when the player has actually told us something. Deliberately
 * whitespace-aware: a field holding three spaces is not content, and
 * treating it as content would let a stray keystroke turn a coach's panel
 * from "nothing here, ask him" into "he told us something".
 */
export function hasAnyHealthContent(record) {
  if (!record) return false;
  return HEALTH_FIELDS.some((k) => typeof record[k] === "string" && record[k].trim() !== "");
}

/**
 * THE sentence — who, right now, can see this record (issue #694). The
 * health-record analogue of `identitySharing.js`'s `describeVisibility`,
 * same shape and same reason: the state has to be readable without
 * inferring it, and `SHARING_NOTICE` alone states the RULE, not who it
 * currently resolves to.
 *
 * Sharing here has no per-section toggle — the whole record goes to every
 * ACTIVE-linked coach automatically — so the dimension `describeVisibility`
 * gets from "which sections are switched on" comes instead from whether the
 * player has written anything at all. That keeps this a genuine four-state
 * function, not one sentence with a name spliced in:
 *
 *   1. NO LINKED COACH, NOTHING WRITTEN — the resting state of a fresh
 *      account. "Nobody can see this."
 *   2. NO LINKED COACH, BUT SOMETHING IS WRITTEN — the trap, and the reason
 *      this function exists rather than a one-line template. Nobody is
 *      reading, but the record goes live for a coach the instant an invite
 *      is accepted, with no further gesture from the player. Saying so is
 *      the difference between a control and a surprise.
 *   3. A LINKED COACH, NOTHING WRITTEN — name them, and say plainly there
 *      is nothing here yet for them to read. Being linked is not the same
 *      as reading something.
 *   4. A LINKED COACH, SOMETHING WRITTEN — name them, and say the record is
 *      visible to them now.
 *
 * `coachNames` MUST be the server's `coach_names` (`GET`/`PUT
 * /players/me/health`, resolved by the same `activeCoachNames` helper the
 * coach's own read is gated on) — never assembled client-side, for the same
 * reason `identitySharing.js` gives: a client-built coach list could
 * disagree with the rule that actually decides who reads.
 *
 * This function does not itself know whether the names it was given loaded
 * successfully — see `HealthInfoCard.jsx`, which calls it ONLY after a
 * successful load (#271): calling it with `coachNames: []` after a failed
 * fetch would render state 1's "Nobody can see this" as fact when the truth
 * is "we don't know".
 */
export function describeHealthAudience({ coachNames, hasContent } = {}) {
  const names = joinNames(coachNames);

  if (!names) {
    if (!hasContent) {
      return "Nobody can see this. You don't have a linked coach right now, and there's nothing written here yet.";
    }
    return (
      "Nobody can see this yet — you don't have a linked coach right now. The moment you link one, everything " +
      "here goes live for them straight away, with no extra step from you."
    );
  }

  if (!hasContent) {
    return `${names} can see this, but there's nothing written here yet.`;
  }

  return `${names} can see this now, including your emergency contact.`;
}

/**
 * The wire payload. ALL SIX KEYS, ALWAYS — the API refuses a partial body
 * (see `routes/playerHealth.ts`), and this is the function that makes that
 * refusal something no legitimate save ever hits. Blanks go as `null`, which
 * is what makes "clear every field" a supported action rather than a stored
 * row of empty strings.
 */
export function toHealthPayload(form) {
  const value = (v) => {
    const trimmed = (v ?? "").trim();
    return trimmed === "" ? null : trimmed;
  };
  return {
    emergency_contact_name: value(form.emergency_contact_name),
    emergency_contact_relationship: value(form.emergency_contact_relationship),
    emergency_contact_phone: value(form.emergency_contact_phone),
    emergency_contact_alt: value(form.emergency_contact_alt),
    medical_conditions: value(form.medical_conditions),
    accessibility_needs: value(form.accessibility_needs),
  };
}

/**
 * The API's `null`s back into the form's `""`s. The round trip has to be
 * exact in both directions or the card's dirty check (which compares
 * payloads, not form state) reports a saved record as unsaved forever.
 */
export function toHealthForm(record) {
  const text = (v) => (typeof v === "string" ? v : "");
  return {
    emergency_contact_name: text(record?.emergency_contact_name),
    emergency_contact_relationship: text(record?.emergency_contact_relationship),
    emergency_contact_phone: text(record?.emergency_contact_phone),
    emergency_contact_alt: text(record?.emergency_contact_alt),
    medical_conditions: text(record?.medical_conditions),
    accessibility_needs: text(record?.accessibility_needs),
  };
}

/**
 * The audit line: WHO saved this record, and WHEN.
 *
 * Every word here is load-bearing, so read the failure modes it is written
 * against before shortening it:
 *
 *   - It says "Saved by <name>" and not "Provided by" or "Reported by".
 *     The server records the ACCOUNT that wrote the row, which in the pilot
 *     population is frequently a parent using the junior's login (#241,
 *     guardian linkage, is open and unbuilt). "Saved by" is true of an
 *     account; "reported by" is a claim about a human we cannot make.
 *   - A record nobody has ever written returns `null` for both, and this
 *     returns `null` in turn — the caller renders the never-filled-in state
 *     rather than a line about nobody.
 *   - `updated_by_name === null` with a real date means the join found no
 *     user row. That is unreachable today (the FK guarantees the user), so
 *     it is rendered as an admission rather than papered over with
 *     "Unknown", which would read like a person's name.
 *
 * Returns a plain string so both surfaces render the identical sentence.
 */
export function formatAuditLine(record) {
  if (!record || !record.updated_at) return null;
  const who = record.updated_by_name
    ? `Saved by ${record.updated_by_name}`
    : "Saved by an account we can no longer identify";
  return `${who} on ${formatAuditDate(record.updated_at)}`;
}

/**
 * A date a coach can act on, in NZ order (day month year) and with the
 * month spelled out — "3/4/2026" is two different days depending on who is
 * reading it, and this is a date somebody may use to decide whether a
 * medical note or a phone number is still current.
 *
 * An unparseable value renders verbatim rather than as "Invalid Date": the
 * raw string is at least honest about what is stored.
 */
export function formatAuditDate(iso) {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  return d.toLocaleDateString("en-NZ", { day: "numeric", month: "long", year: "numeric" });
}

/**
 * What one field renders as. `null` for absent — the caller decides how to
 * present `NOT_PROVIDED`, because a value and an absence must not look
 * alike (the panel greys and italicises the absence; a shared function
 * returning the string for both would let a future surface render them
 * identically).
 */
export function displayValue(v) {
  return typeof v === "string" && v.trim() !== "" ? v : null;
}

/**
 * The emergency contact as a coach reads it: who, how they're related, and
 * the number — with any part that is missing simply left out rather than
 * padded with placeholders.
 *
 * Returns `null` only when ALL FOUR parts are empty, so the panel can say
 * "Not provided" once instead of four times. Any single part on its own is
 * still worth showing: a number with no name is still a number to ring, and
 * a name with no number is still a person to ask the player about.
 */
export function formatEmergencyContact(record) {
  const name = displayValue(record?.emergency_contact_name);
  const relationship = displayValue(record?.emergency_contact_relationship);
  const phone = displayValue(record?.emergency_contact_phone);
  const alt = displayValue(record?.emergency_contact_alt);
  if (!name && !relationship && !phone && !alt) return null;

  const who = name && relationship ? `${name} (${relationship})` : name || (relationship ? `A ${relationship}` : null);
  const numbers = [phone, alt].filter(Boolean).join(" · ");
  if (who && numbers) return `${who} — ${numbers}`;
  return who || numbers;
}
